import React, { useEffect, useState } from "react";
import { DrawerModal } from "../drawer-modal/drawer-modal";
import { Title } from "@src/components/atoms/title/title";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";

import { DrawerButtonWrapper } from "@src/components/molecules/drawer-button-wrapper/drawer-button-wrapper";
import { useDispatch } from "react-redux";
import {
  updateModalsCloseDrawerModal,
  updateUserListResultsReady,
} from "@src/reducers/your-reducer";
import { Button } from "@src/components/atoms/buttons/button/button";

import styles from "./add-service-user-drawer-modal.module.scss";
import { Input2025 } from "@src/components/atoms/input-2025/input-2025";
import { RoleSelector } from "@src/components/molecules/roles-selector/roles-selector";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { trimUserInputs } from "@src/cloud-connect/user-manager/utils/trim-user-drawer-inputs";
import { validateUserInputs } from "@src/cloud-connect/user-manager/utils/validate-user-drawer-inputs";
import { postCreateServiceUserUseCase } from "@src/cloud-connect/user-manager/use-cases/post-create-service-user-use-case";
import { DatePicker } from "@src/components/atoms/date-picker/date-picker";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { UserCredentialsModal } from "@src/components/organisms/modals/user-credentials-modal/user-credentials-modal";
import {
  Role,
  UserCredentials,
  ZitadelServiceUser,
  ZitadelUserType,
} from "@src/types/user-management";
import { getValidUserRolesUseCase } from "@src/cloud-connect/user-manager/use-cases/get-valid-user-roles-use-case";
import { useI18N } from "@src/language/language-context";
import Spinner from "@src/components/atoms/spinner/spinner";

const cssClassName = "add-user-drawer-modal";

export const AddServiceUserDrawerModal = () => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const texts = {
    addServiceUserTitle: i18n(
      "modules.addServiceUserDrawerModal.addServiceUserTitle",
    ),
    serviceUserUsernameLabel: i18n(
      "modules.addServiceUserDrawerModal.serviceUserUsernameLabel",
    ),
    serviceUserUsernameInputPlaceholder: i18n(
      "modules.addServiceUserDrawerModal.serviceUserUsernameInputPlaceholder",
    ),
    serviceUserNameLabel: i18n(
      "modules.addServiceUserDrawerModal.serviceUserNameLabel",
    ),
    serviceUserNameInputPlaceholder: i18n(
      "modules.addServiceUserDrawerModal.serviceUserNameInputPlaceholder",
    ),
    serviceUserDescriptionLabel: i18n(
      "modules.addServiceUserDrawerModal.serviceUserDescriptionLabel",
    ),
    serviceUserDescriptionInputPlaceholder: i18n(
      "modules.addServiceUserDrawerModal.serviceUserDescriptionInputPlaceholder",
    ),
    createServiceUserButtonText: i18n(
      "modules.addServiceUserDrawerModal.createServiceUserButtonText",
    ),
    cancelButtonText: i18n(
      "modules.addServiceUserDrawerModal.cancelButtonText",
    ),
    rolesWarningText: i18n(
      "modules.addServiceUserDrawerModal.rolesWarningText",
    ),
    inputsCheckWarningText: i18n(
      "modules.addServiceUserDrawerModal.inputsCheckWarningText",
    ),
    userCreatedSuccessText: i18n(
      "modules.addServiceUserDrawerModal.userCreatedSuccessText",
    ),
    userCreatedErrorText: i18n(
      "modules.addServiceUserDrawerModal.userCreatedErrorText",
    ),
    expirationDatePlaceholder: i18n(
      "modules.addServiceUserDrawerModal.expirationDatePlaceholder",
    ),
  };

  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [creatingServiceUser, setCreatingServiceUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [credentialsData, setCredentialsData] =
    useState<UserCredentials | null>(null);

  const [serviceUser, setServiceUser] =
    useState<Partial<ZitadelServiceUser> | null>({
      id: null,
      type: ZitadelUserType.SERVICE_API,
      username: "",
      name: "",
      description: "",
      roles: [],
      expiration_date: "",
    });

  const closeDrawer = () => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: false,
        id: "addServiceUserDrawerModal",
      }),
    );
  };

  const handleAddServiceUser = async () => {
    setCreatingServiceUser(true);

    if (serviceUser.roles.length === 0) {
      warningSnackbar(texts.rolesWarningText, 5000);
      setCreatingServiceUser(false);
      return;
    }

    const trimmedUser = trimUserInputs(serviceUser);

    const { isValid, errors } = validateUserInputs(trimmedUser);

    if (!isValid) {
      warningSnackbar(
        `${texts.inputsCheckWarningText}: ${errors.join(", ")}`,
        5000,
      );
      setCreatingServiceUser(false);
      return;
    }

    const response = await postCreateServiceUserUseCase(
      trimmedUser as ZitadelServiceUser,
    );

    const dateToTriggerReload = new Date().toISOString();

    if (response.success && response.data) {
      successSnackbar(texts.userCreatedSuccessText, 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));

      // Show credentials modal if credentials are returned
      if (
        response.data.id &&
        response.data.key_id &&
        response.data.private_key &&
        response.data.expiration_date
      ) {
        setCredentialsData({
          id: response.data.id,
          key_id: response.data.key_id,
          private_key: response.data.private_key,
          expiration_date: response.data.expiration_date,
        });
      } else {
        closeDrawer();
      }
    } else {
      errorSnackbar(response.error || texts.userCreatedErrorText, 5000);
      dispatch(updateUserListResultsReady(dateToTriggerReload));
      closeDrawer();
    }

    setCreatingServiceUser(false);
  };

  const handleCloseCredentialsModal = () => {
    setCredentialsData(null);
    closeDrawer();
  };

  useEffect(() => {
    const loadValidUserRoles = async () => {
      setLoading(true);
      const roles = await getValidUserRolesUseCase();
      setRoleOptions(roles.data || []);
      setLoading(false);
    };

    loadValidUserRoles();
  }, []);

  return (
    <>
      <DrawerModal id={"addServiceUserDrawerModal"}>
        <div className={styles[cssClassName]}>
          <FlexColumnContainer verticalGap={32}>
            <Title tag={"h2"} text={texts.addServiceUserTitle} />
            {loading ? (
              <Spinner />
            ) : (
              <div className={styles[`${cssClassName}__content-wrapper`]}>
                <Input2025
                  labelText={texts.serviceUserUsernameLabel}
                  inputPlaceHolder={texts.serviceUserUsernameInputPlaceholder}
                  inputValue={serviceUser.username || ""}
                  onChangeFunc={(e) =>
                    setServiceUser({ ...serviceUser, username: e.target.value })
                  }
                />
                <Input2025
                  labelText={texts.serviceUserNameLabel}
                  inputPlaceHolder={texts.serviceUserNameInputPlaceholder}
                  inputValue={serviceUser.name || ""}
                  onChangeFunc={(e) =>
                    setServiceUser({ ...serviceUser, name: e.target.value })
                  }
                />
                <Input2025
                  labelText={texts.serviceUserDescriptionLabel}
                  inputPlaceHolder={
                    texts.serviceUserDescriptionInputPlaceholder
                  }
                  inputValue={serviceUser.description || ""}
                  onChangeFunc={(e) =>
                    setServiceUser({
                      ...serviceUser,
                      description: e.target.value,
                    })
                  }
                />
                <RoleSelector
                  user={serviceUser}
                  setUser={setServiceUser}
                  roleOptions={roleOptions}
                />
                <DatePicker
                  selectedDate={serviceUser.expiration_date || null}
                  onDateChange={(date) =>
                    setServiceUser({ ...serviceUser, expiration_date: date })
                  }
                  placeholder={texts.expirationDatePlaceholder}
                  disablePastDates={true}
                />
                <DrawerButtonWrapper>
                  <ButtonWithLoader
                    fun={handleAddServiceUser}
                    text={texts.createServiceUserButtonText}
                    variant={"full-width"}
                    loading={creatingServiceUser}
                  />
                  <Button
                    fun={closeDrawer}
                    text={texts.cancelButtonText}
                    variant={["secondary", "full-width"]}
                  />
                </DrawerButtonWrapper>
              </div>
            )}
          </FlexColumnContainer>
        </div>
      </DrawerModal>

      {credentialsData && (
        <UserCredentialsModal
          id={credentialsData.id}
          key_id={credentialsData.key_id}
          private_key={credentialsData.private_key}
          expiration_date={credentialsData.expiration_date}
          onClose={handleCloseCredentialsModal}
        />
      )}
    </>
  );
};
